import { hasDocsPage, localeHasDocs } from '~/utils/docs-manifest';

export type DocLocale = 'en' | 'ja';

export function toDocLocale(loc: string): DocLocale {
  return loc === 'ja' ? 'ja' : 'en';
}

/**
 * Shared route parsing for `/help/*` pages and the docs layout.
 */
export function useDocsRoute() {
  const route = useRoute();
  const { locale: appLocale } = useI18n();

  const slugParts = computed((): string[] => {
    const path = route.path.replace(/\/+$/, '') || '/';
    if (!path.startsWith('/help')) {
      return [];
    }
    const rest = path.slice('/help'.length).replace(/^\/+/, '');
    if (!rest) {
      return [];
    }
    return rest.split('/').filter(Boolean);
  });

  const logicalPath = computed(() => slugParts.value.join('/'));

  function defaultDocLocale(): DocLocale {
    const loc = appLocale.value;
    return toDocLocale(localeHasDocs(loc) ? loc : 'en');
  }

  const ready = computed(() => {
    const parts = slugParts.value;
    if (parts.length === 0) {
      return false;
    }
    return localeHasDocs(parts[0]);
  });

  const docLocale = computed((): DocLocale => {
    const first = slugParts.value[0];
    if (first && localeHasDocs(first)) {
      return toDocLocale(first);
    }
    return defaultDocLocale();
  });

  function pathTailAfterLocale(): string {
    const parts = slugParts.value;
    if (parts.length > 0 && localeHasDocs(parts[0])) {
      return parts.slice(1).join('/');
    }
    return '';
  }

  function helpPathForLocale(locale: string): string {
    const tail = pathTailAfterLocale();
    const logical = tail ? `${locale}/${tail}` : locale;
    const target = hasDocsPage(logical) ? logical : locale;
    return `/help/${target}`;
  }

  return {
    slugParts,
    logicalPath,
    ready,
    docLocale,
    defaultDocLocale,
    pathTailAfterLocale,
    helpPathForLocale,
  };
}
